import type { ReactNode } from 'react';
import { Avatar } from '../primitives/Avatar.tsx';
import './sidebar.css';

export interface SidebarProps {
  /** Brand lockup at the top. */
  brand: ReactNode;
  /** Nav items. */
  children: ReactNode;
  /** Account card pinned to the bottom. */
  footer: ReactNode;
}

export function Sidebar({ brand, children, footer }: SidebarProps) {
  return (
    <div className="sidebar">
      {brand}
      <nav className="sidebarNav" aria-label="Portal sections">
        {children}
      </nav>
      <div className="sidebarFooter">{footer}</div>
    </div>
  );
}

export interface SidebarBrandProps {
  logoSrc: string;
  /** "Partner Portal", or "Partner Portal Admin" for internal staff. */
  label: string;
  /** Internal staff get the amber label, partners the cyan one. */
  tone?: 'partner' | 'admin';
}

export function SidebarBrand({ logoSrc, label, tone = 'partner' }: SidebarBrandProps) {
  return (
    <div className="sidebarBrand">
      <img src={logoSrc} alt="" width={30} height={30} />
      <span className="sidebarBrandStack">
        <span className="sidebarWordmark">sparkery</span>
        <span className="sidebarBrandLabel" data-tone={tone}>
          {label}
        </span>
      </span>
    </div>
  );
}

export interface NavItemProps {
  icon: ReactNode;
  label: string;
  active?: boolean;
  /** Rendered as whatever the router provides — a Link, or a button in a story. */
  as?: React.ElementType;
  href?: string;
  onClick?: () => void;
}

/**
 * One navigation row.
 *
 * A real link or button, never a clickable div. The prototype used divs with onClick,
 * which are invisible to keyboards and to screen readers — and navigation is the last
 * place that should be.
 */
export function NavItem({ icon, label, active, as, href, onClick }: NavItemProps) {
  const Tag = as ?? (href ? 'a' : 'button');

  return (
    <Tag
      className="navItem"
      data-active={active ? 'true' : 'false'}
      aria-current={active ? 'page' : undefined}
      {...(href ? { href, to: href } : { type: 'button' })}
      {...(onClick ? { onClick } : {})}
    >
      <span className="navItemIcon" aria-hidden="true">
        {icon}
      </span>
      {label}
    </Tag>
  );
}

export interface AccountCardProps {
  initials: string;
  name: string;
  meta: string;
  metaTone?: 'muted' | 'admin';
  onSignOut: () => void;
  signOutIcon: ReactNode;
}

export function AccountCard({
  initials,
  name,
  meta,
  metaTone = 'muted',
  onSignOut,
  signOutIcon,
}: AccountCardProps) {
  return (
    <div className="accountCard">
      {/* Sidebar account identity always uses the design-system footer wash treatment. */}
      <Avatar initials={initials} name={name} tone="cyan-soft" size="sm" />
      <span className="accountText">
        <span className="accountName">{name}</span>
        <span className="accountMeta" data-tone={metaTone}>
          {meta}
        </span>
      </span>
      <button type="button" className="accountSignOut" onClick={onSignOut} aria-label="Sign out">
        {signOutIcon}
      </button>
    </div>
  );
}
